import { useNavigate } from "react-router-dom";
import { Phone, Calendar, ArrowUpRight } from "lucide-react";
import { BUSINESS } from "@/config";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { useDemoGate } from "@/components/DemoGate";

// Book Online: opens external booking engine if configured, else routes to /book
export function useBook() {
  const navigate = useNavigate();
  const { gate } = useDemoGate();
  return (category) => {
    gate(() => {
      track("book_online_click", {
        service: category || "general",
        destination: BUSINESS.bookingUrl ? "external_calendar" : "onsite_book_page",
      });
      if (BUSINESS.bookingUrl) {
        window.open(BUSINESS.bookingUrl, "_blank", "noopener,noreferrer");
      } else {
        navigate(category ? `/book?service=${category}` : "/book");
      }
    });
  };
}

export function BookButton({ label = "Book Online", category, className = "", testid = "book-online-button", solid = true }) {
  const book = useBook();
  return (
    <button
      data-testid={testid}
      onClick={() => book(category)}
      className={cn(
        "group inline-flex items-center gap-2 px-7 py-4 font-mono uppercase text-xs tracking-[0.18em] transition-transform duration-300 hover:-translate-y-0.5",
        solid
          ? "bg-primary text-primary-foreground hover:shadow-[0_10px_40px_-12px_hsl(343_66%_34%)]"
          : "border border-foreground/40 text-foreground hover:border-foreground",
        className
      )}
    >
      <Calendar className="h-4 w-4" strokeWidth={1.5} />
      {label}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
    </button>
  );
}

export function CallButton({ label, className = "", testid = "call-text-button" }) {
  return (
    <a
      data-testid={testid}
      href={BUSINESS.phoneHref}
      onClick={() => track("phone_click", { location: "call_button" })}
      className={cn(
        "group inline-flex items-center gap-2 border border-foreground/50 bg-background/20 backdrop-blur-sm px-7 py-4 font-mono uppercase text-xs tracking-[0.18em] text-foreground transition-colors duration-300 hover:border-foreground hover:bg-background/40",
        className
      )}
    >
      <Phone className="h-4 w-4" strokeWidth={1.5} />
      {label || `Call / Text · ${BUSINESS.phoneDisplay}`}
    </a>
  );
}

// Reusable CTA pair used at the end of sections/pages
export function CTAGroup({ category, className = "" }) {
  return (
    <div className={cn("flex flex-col sm:flex-row items-start sm:items-center gap-4", className)}>
      <BookButton category={category} />
      <CallButton label="Call / Text Us" />
    </div>
  );
}
